import React, { useEffect, useRef, useState } from "react";
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, HStack, Button, Input, FormControl, FormLabel, Select, VStack, Icon, Text, Tag, TagLabel, TagCloseButton, useColorModeValue, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, SimpleGrid, Spacer } from "@chakra-ui/react";
import { saApi } from "../../api/superAdminApi";
import { FaCloudUploadAlt } from "react-icons/fa";

const Templates = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [buId, setBuId] = useState("");

  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [docType, setDocType] = useState("CV");
  const [uploadCompanyId, setUploadCompanyId] = useState("");
  const [uploadBuId, setUploadBuId] = useState("");
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const bgHover = useColorModeValue("gray.50", "gray.700");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const resetUploadForm = () => {
    setFile(null);
    setName("");
    setDescription("");
    setDocType("CV");
    setUploadCompanyId("");
    setUploadBuId("");
    setDragActive(false);
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await saApi.listTemplates({ company_id: companyId || undefined, business_unit_id: buId || undefined });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async () => {
    if (!file || !uploadCompanyId || !name) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("company_id", String(uploadCompanyId));
    fd.append("name", name);
    if (description) fd.append("description", description);
    if (docType) fd.append("doc_type", docType);
    if (uploadBuId) fd.append("business_unit_id", String(uploadBuId));
    await saApi.uploadTemplate(fd);
    resetUploadForm();
    await load();
  };

  const handleCreate = async () => {
    await upload();
    onClose();
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const f = e.dataTransfer.files[0];
      setFile(f);
    }
  };

  const remove = async (id) => {
    await saApi.deleteTemplate(id);
    await load();
  };

  return (
    <Box>
      <Heading size="md" mb={4}>Templates</Heading>

      <HStack spacing={3} mb={6} align="flex-end">
        <FormControl maxW="200px">
          <FormLabel>Company ID</FormLabel>
          <Input value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder="e.g. 1" />
        </FormControl>
        <FormControl maxW="200px">
          <FormLabel>Business Unit ID</FormLabel>
          <Input value={buId} onChange={(e) => setBuId(e.target.value)} placeholder="optional" />
        </FormControl>
        <Button onClick={load}>Filter</Button>
        <Spacer />
        <Button colorScheme="teal" onClick={onOpen}>Create a new template</Button>
      </HStack>


      <Modal isOpen={isOpen} onClose={() => { onClose(); resetUploadForm(); }} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create a new template</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <FormControl isRequired>
                <FormLabel m={0}>Company ID</FormLabel>
                <Input value={uploadCompanyId} onChange={(e) => setUploadCompanyId(e.target.value)} placeholder="e.g. 1" />
              </FormControl>
              <FormControl>
                <FormLabel m={0}>Business Unit ID</FormLabel>
                <Input value={uploadBuId} onChange={(e) => setUploadBuId(e.target.value)} placeholder="optional" />
              </FormControl>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
              <FormControl isRequired>
                <FormLabel m={0}>Name</FormLabel>
                <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel m={0}>Description</FormLabel>
                <Input placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
              </FormControl>
            </SimpleGrid>

            <FormControl isRequired>
              <FormLabel m={0}>File</FormLabel>
              <Box
                border="2px dashed"
                borderColor={dragActive ? "blue.400" : borderColor}
                borderRadius="md"
                px={4}
                py={6}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bg={dragActive ? "blue.50" : "transparent"}
                transition="all 0.2s"
                cursor="pointer"
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                _hover={{ bg: bgHover }}
              >
                <HStack spacing={2} align="center">
                  <Icon as={FaCloudUploadAlt} boxSize={5} color="gray.400" />
                  <Text fontSize="sm" color="gray.600" lineHeight="1">Drag & drop or click to upload</Text>
                </HStack>
                <Input ref={fileInputRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} display="none" />
              </Box>
              {file && (
                <Tag size="sm" mt={2} variant="subtle" colorScheme="gray">
                  <TagLabel>{file.name}</TagLabel>
                  <TagCloseButton onClick={() => setFile(null)} />
                </Tag>
              )}
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button onClick={() => { onClose(); resetUploadForm(); }}>Cancel</Button>
              <Button colorScheme="teal" onClick={handleCreate} isDisabled={!file || !uploadCompanyId || !name}>Create template</Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {loading ? (
        <Spinner />
      ) : error ? (
        <Box color="red.500">{error}</Box>
      ) : (
        <Table variant="simple" bg="white" borderWidth="1px" borderRadius="md">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Company</Th>
              <Th>BU</Th>
              <Th>Name</Th>
              <Th>Doc type</Th>
              <Th>Description</Th>
              <Th>Path</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {items.map((t) => (
              <Tr key={t.id}>
                <Td>{t.id}</Td>
                <Td>{t.company_id}</Td>
                <Td>{t.business_unit_id || "-"}</Td>
                <Td>{t.name}</Td>
                <Td>{t.doc_type}</Td>
                <Td>{t.description || ""}</Td>
                <Td>{t.gcs_path}</Td>
                <Td><Button size="sm" onClick={() => remove(t.id)}>Delete</Button></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}
    </Box>
  );
};

export default Templates;


